<template>
   <div class="py-10 px-4 sm:px-6 lg:px-24">
      <div class="sm:flex sm:items-center">
         <div class="sm:flex-auto">
            <h1 class="text-base font-semibold leading-6 text-gray-900">Sub-Agents</h1>
            <p class="mt-2 text-sm text-gray-700">A list of all sub agents under your account</p>
         </div>
         <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button @click="handleAdd" type="button" class="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
               Add Sub Agent
            </button>
         </div>
      </div>

      <!-- Search & Filters Toolbar -->
      <div class="mt-6 flex flex-col md:flex-row gap-4 items-center justify-between bg-gray-50/50 p-4 rounded-xl border border-gray-150 mb-6">
         <!-- Search Input -->
         <div class="relative w-full md:w-80">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
               <i class="pi pi-search text-xs"></i>
            </span>
            <input v-model="searchQuery" type="text" placeholder="Search by name, email or phone..." class="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300">
         </div>

         <!-- Date Filters -->
         <div class="flex flex-wrap gap-4 items-center w-full md:w-auto justify-end">
            <!-- Dropdown Scope Filter -->
            <div class="relative w-full sm:w-72 flex items-center gap-2">
               <span class="text-xs font-semibold text-gray-500 whitespace-nowrap">Filter Bookings:</span>
               <div class="relative flex-grow">
                  <select v-model="selectedScope" @change="fetchSubAgents" class="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none transition-all duration-300 cursor-pointer">
                     <option v-for="scope in scopes" :key="scope.id" :value="scope.id">{{ scope.name }}</option>
                  </select>
                  <span class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                     <i class="pi pi-chevron-down text-xs"></i>
                  </span>
               </div>
            </div>

            <!-- Custom date range inputs -->
            <div v-if="selectedScope === 'custom'" class="flex items-center gap-2 w-full sm:w-auto">
               <input type="date" v-model="startDate" @change="fetchSubAgents" class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
               <span class="text-gray-400 text-sm">to</span>
               <input type="date" v-model="endDate" @change="fetchSubAgents" class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            </div>
         </div>
      </div>

      <div class="mt-8 flow-root">
         <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
               <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <div v-if="isLoading && subs.length === 0" class="text-center my-8 font-medium">...loading data. please wait</div>
                  <table v-else class="min-w-full divide-y divide-gray-300">
                     <thead class="bg-gray-50">
                        <tr>
                           <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
                           <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email / Phone</th>
                           <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Loyalty Points</th>
                           <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Bookings</th>
                           <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 pr-6">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody class="divide-y divide-gray-200 bg-white">
                        <tr v-for="item in filteredSubs" :key="item.id">
                           <td @click="$router.push({name: 'AccountDashboard', params: {'agent': item.id}})" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 capitalize cursor-pointer text-indigo-600 hover:text-indigo-900 ">{{ item.name }}</td>
                           <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <div>{{ item.email }}</div>
                              <div class="text-xs text-gray-400 mt-0.5">{{ item.phone }}</div>
                           </td>
                           <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ item.loyalty_points }}</td>
                           <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ item.booking_count }}</td>
                           <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <span @click="downloadReport(item)" class="text-indigo-600 hover:text-indigo-900 cursor-pointer font-semibold">Booking Report</span> | 
                              <span @click="$router.push({name: 'UserBookings', params: {'userType' : 'agent', 'subUserId' : item.id}})" class="text-indigo-600 hover:text-indigo-900 cursor-pointer">Bookings</span> | 
                              <span @click="handleEdit(item)" class="text-indigo-600 hover:text-indigo-900 cursor-pointer">Edit</span> | 
                              <span @click="handleToggleStatus(item)" class="text-indigo-600 hover:text-indigo-900 cursor-pointer">
                                 {{ item.status ? 'Deactivate' : 'Activate' }}
                              </span>
                           </td>
                        </tr>
                        <tr v-if="filteredSubs && filteredSubs.length == 0">
                           <td colspan="5" class="text-center whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              No sub agents created yet   
                           </td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </div>
         </div>
      </div>
   </div>
   <ConfirmationModal :show="openConfirmModal" @closed="handleConfirmClose">
      <template v-slot:buttons>
         <button @click="askingConfirmed" type="button" :class="[isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer', confirmActionType === 'deactivate' ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500']" class="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm text-white shadow-sm xs:ml-3 xs:w-auto capitalize">{{ confirmActionType }}</button>
         <button @click="openConfirmModal = false" type="button" class="mt-3 inline-flex w-full cursor-pointer justify-center rounded-md bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 xs:mt-0 xs:w-auto">Cancel</button>
      </template>
   </ConfirmationModal>
   <SlideOver :show="openSaveDialog" @closed="openSaveDialog = false">
      <template v-slot:content>
         <SaveSubAgent :data="selectedSubAgent" @success="handleSaveSuccess" @error="handleSaveError" />
      </template>
   </SlideOver>
   <Notification :show="notif.show" :type="notif.type" :message="notif.message" :messageDetails="notif.messageDetails" @closed="notifClosed"/>


</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { handleApiError, getUserToken, getBackendBaseUrl } from '@/utils/common'
import SlideOver from '@/widgets/SlideOver.vue'
import Agent from '@/composables/admin/agent'
import { NotificationType } from '@/types/index'
import Notification from '@/widgets/Notification.vue'
import SaveSubAgent from '@/components/agent/SaveSubAgent.vue'
import { Agent as AgentType } from '@/types/admin'
import ConfirmationModal from '@/widgets/ConfirmationModal.vue'
import axios from 'axios'

const isLoading = ref<boolean>(false)
const openSaveDialog = ref<boolean>(false)
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})
const { subAgents, updateSubAgent } = Agent()
const subs = ref<AgentType[]>([])
const selectedSubAgent = ref<AgentType | null>(null)
const openConfirmModal = ref<boolean>(false)
const toggleItem = ref<AgentType | null>(null)
const confirmActionType = ref<'deactivate' | 'activate'>('deactivate')
const baseUrl = getBackendBaseUrl()

// Search & Filter variables
const searchQuery = ref<string>('')
const selectedScope = ref<string>('this_month')
const startDate = ref<string>('')
const endDate = ref<string>('')

const scopes = ref([
   { id: 'all_time', name: 'Lifetime' },
   { id: 'today', name: 'Today' },
   { id: 'this_week', name: 'This Week' },
   { id: 'this_month', name: 'This Month' },
   { id: 'last_month', name: 'Last Month' },
   { id: 'this_year', name: 'This Year' },
   { id: 'last_year', name: 'Last Year' },
   { id: 'custom', name: 'Custom Date Range' }
])

onMounted(() => {
   mounted() 
})

const mounted = async () => {
   await fetchSubAgents()
}

const handleSaveSuccess = async () => {
   openSaveDialog.value = false
   notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Your sub agent has been added successfully'}
   setTimeout(() => { notifClosed() }, 4000)
   
   await fetchSubAgents()
}

const handleSaveError = (error: string) => { 
   notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
   setTimeout(() => { notifClosed() }, 4000)
}

const handleAdd = () => {
   selectedSubAgent.value = null
   openSaveDialog.value = true
}

const handleEdit = (agent: AgentType) => {
   selectedSubAgent.value = agent
   openSaveDialog.value = true
}

const notifClosed = () : void => {
   notif.value.show = false
}

const handleToggleStatus = (item: AgentType) => {
   toggleItem.value = item
   confirmActionType.value = item.status ? 'deactivate' : 'activate'
   openConfirmModal.value = true
}

const askingConfirmed = async () => {
   if (!isLoading.value && toggleItem.value) {
      isLoading.value = true
      const payload: any = {
         id: toggleItem.value.id,
         name: toggleItem.value.name,
         phone: toggleItem.value.phone,
         email: toggleItem.value.email,
         commission: toggleItem.value.commission,
         website_login: toggleItem.value.website_login,
         is_admin: toggleItem.value.is_admin,
         active: toggleItem.value.status ? 0 : 1,
      }
      const response = await updateSubAgent(payload)

      if (response.status == 200) {
         notif.value = { show: true, type: 'success', message: 'Success', messageDetails: `Sub agent ${confirmActionType.value}d successfully`}
         setTimeout(() => { notifClosed() }, 4000)
         await fetchSubAgents()
      } else {
         const error = handleApiError(response)
         notif.value = { show: true, type: 'error', message: 'Error', messageDetails: error}
         setTimeout(() => { notifClosed() }, 4000)
      }

      isLoading.value = false
      openConfirmModal.value = false
   }  
}

const handleConfirmClose = () => openConfirmModal.value = false

const fetchSubAgents = async () => {
   if (selectedScope.value === 'custom') {
      if (!startDate.value || !endDate.value) {
         return
      }
   }

   isLoading.value = true

   let formattedStart = ''
   let formattedEnd = ''
   if (selectedScope.value === 'custom') {
      const format = (dateStr: string) => {
         const parts = dateStr.split('-')
         return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      formattedStart = format(startDate.value)
      formattedEnd = format(endDate.value)
   }

   const response = await subAgents(selectedScope.value, formattedStart, formattedEnd)
   isLoading.value = false

   if (response.status == 200) {
      subs.value = response.data
   } else {
      const error = handleApiError(response)
      console.log(error)
   }
}

const filteredSubs = computed(() => {
   const query = searchQuery.value.toLowerCase().trim()
   if (!query) return subs.value
   return subs.value.filter(item => 
      item.name.toLowerCase().includes(query) ||
      item.email.toLowerCase().includes(query) ||
      (item.phone && item.phone.toLowerCase().includes(query))
   )
})

const downloadReport = async (agent: AgentType) => {
   let finalScope = selectedScope.value
   if (selectedScope.value === 'custom') {
      if (!startDate.value || !endDate.value) {
         notif.value = { show: true, type: 'error', message: 'Date Missing', messageDetails: 'Please select both start and end dates first.'}
         setTimeout(() => { notifClosed() }, 4000)
         return
      }
      
      const format = (dateStr: string) => {
         const parts = dateStr.split('-')
         return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      finalScope = `custom_${format(startDate.value)}_${format(endDate.value)}`
   }

   try {
      isLoading.value = true
      const url = `${baseUrl}api/agent/reports/sub-agent/bookings/${agent.id}/${finalScope}/download`
      const response = await axios.get(url, {
         headers: {
            'Authorization': `Bearer ${getUserToken()}`
         },
         responseType: 'blob'
      })

      let filename = `Booking_Report_${agent.name.replace(/\s+/g, '_')}_${finalScope}.csv`
      const disposition = response.headers['content-disposition']
      if (disposition && disposition.indexOf('attachment') !== -1) {
         const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
         const matches = filenameRegex.exec(disposition)
         if (matches != null && matches[1]) { 
            filename = matches[1].replace(/['"]/g, '')
         }
      }

      const blob = new Blob([response.data], { type: 'text/csv' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Booking report downloaded successfully'}
      setTimeout(() => { notifClosed() }, 4000)
   } catch (error) {
      notif.value = { show: true, type: 'error', message: 'Download Failed', messageDetails: 'Failed to download booking report. Please try again.'}
      setTimeout(() => { notifClosed() }, 5000)
      console.error(error)
   } finally {
      isLoading.value = false
   }
}
</script>